/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import { trackEventBeforeNavigation } from '@aglyn/aglyn/app-utils/analytics-events'
import { generateOrgSlug } from '@aglyn/aglyn'
import { useSnackbar } from '@aglyn/shared-ui-snackstack'
import { useUser } from '@aglyn/tenant-feature-instance'
import { authorizedFetch } from '@aglyn/shared-util-http/authorized-token'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { buildRoute, Route } from '../constants/route-links'

const WORKSPACE_DOMAIN = process.env.NEXT_PUBLIC_WORKSPACE_DOMAIN ?? 'aglyn.com'

export interface CreateOrgDialogProps {
  open: boolean
  onClose: () => void
  /**
   * Seed the name field on first open (AGL-1523): the workspace picker
   * re-offers the org name a failed signup-time provisioning collected, so
   * the person doesn't have to type it a second time. Editable as ever.
   */
  initialName?: string
  /**
   * Where to land once the workspace exists, given its reserved slug.
   * Defaults to the new workspace's sites.
   *
   * The org jump page passes a builder that carries the marketing plan intent
   * (AGL-1117), so a workspace created while `?plan=` is on the URL opens on
   * billing with that plan rather than on an empty site list. The dialog does
   * not read the intent itself: the page that owns the intent owns the
   * destination, and the org switcher — which has no intent — keeps the
   * default.
   */
  destination?: (orgSlug: string) => string
}

/**
 * Create-an-organization dialog (AGL-621), shared by the org switcher and
 * the org jump page. Posts to /api/orgs/create, then lands in the new
 * workspace by URL — `/[orgSlug]/hosts` on the apex, or the org's own
 * subdomain when workspace subdomains are live.
 */
export function CreateOrgDialog(props: CreateOrgDialogProps) {
  const { open, onClose, initialName, destination } = props
  const { data: user } = useUser()
  const router = useRouter()
  const { enqueueSnackbar } = useSnackbar()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [busy, setBusy] = useState(false)

  // Seed once, on the first open that has a name to offer — never over what
  // the person has already typed (AGL-1523).
  const seededRef = useRef(false)
  useEffect(() => {
    if (!open || seededRef.current || !initialName) return
    seededRef.current = true
    setName((current) => current || initialName)
    setSlug((current) => current || generateOrgSlug(initialName))
  }, [open, initialName])

  const reset = () => {
    setName('')
    setSlug('')
    setSlugTouched(false)
  }

  const handleCreate = async () => {
    if (!name.trim() || busy) return
    setBusy(true)
    try {
      const response = await authorizedFetch(user, '/api/orgs/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), slug: slug.trim() }),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok || !payload?.orgId) {
        enqueueSnackbar(payload?.error ?? 'Creating the organization failed', {
          variant: response.status === 409 ? 'warning' : 'error',
        })
        return
      }
      // Activation (AGL-1561). After the `payload.orgId` guard above, so a
      // 409 slug collision — where no org was created — is never counted.
      // The org name is deliberately NOT a param: it is customer identity.
      //
      // Navigation-safe, like the signup door (AGL-2587): the `router.push`
      // below is soft on the apex, but lands on the workspace's OWN
      // SUBDOMAIN once workspace subdomains are live, and that is a full
      // document navigation which tears down an async transport's pending
      // hit. Costs an already-settled transport nothing.
      await trackEventBeforeNavigation('org_created', {})
      enqueueSnackbar(`Created "${name.trim()}"`, { variant: 'success' })
      reset()
      onClose()
      // The server returns the reserved slug — land in the new workspace.
      const createdSlug = payload.slug ?? slug.trim()
      router.push(
        destination?.(createdSlug) ??
          buildRoute(Route.HOST_LIST, { orgSlug: createdSlug }),
      )
    } catch (error) {
      console.error(error)
      enqueueSnackbar('Creating the organization failed', { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => (busy ? null : onClose())}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{'Create an organization'}</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          {'Organizations own sites and share media, data, plugins and ' +
            'billing across them. You become the owner.'}
        </Typography>
        <TextField
          label="Organization name"
          value={name}
          onChange={(event) => {
            setName(event.target.value)
            if (!slugTouched) setSlug(generateOrgSlug(event.target.value))
          }}
          autoFocus
        />
        <TextField
          label="Workspace URL"
          value={slug}
          onChange={(event) => {
            setSlug(event.target.value.toLowerCase())
            setSlugTouched(true)
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">{`.${WORKSPACE_DOMAIN}`}</InputAdornment>
              ),
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {'Cancel'}
        </Button>
        <Button
          variant="contained"
          disabled={busy || !name.trim()}
          onClick={() => void handleCreate()}
        >
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
CreateOrgDialog.displayName = 'CreateOrgDialog'

export default CreateOrgDialog
